<template>
  <aside class="participant-editor">
    <header>
      <h2>{{ title }}</h2>
      <button type="button" class="light" @click="$emit('close')">Close</button>
    </header>

    <pd-details v-if="model" :model="model" />

    <p v-if="error" class="note error">{{ error }}</p>
  </aside>
</template>

<style lang="scss">
.participant-editor {
  min-width: 390px;
  background: var(--app-bgcolor);
  border-left: 1px solid var(--text-dim3-color);
  overflow: auto;

  > header {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    background: var(--app-bgcolor);
    border-bottom: 1px solid var(--text-dim3-color);
  }

  h2 {
    margin: 0;
    min-width: 0;
    font-size: 1.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pd-details {
    margin: 1rem;
  }

  .note.error {
    margin: 1rem;
  }
}
</style>

<script>
import { computed, toRef } from 'vue'
import schema from '@/schema.js'
import { createModelProxy } from '@/momentum-model-editor.js'
import PdDetails from '@/views/participant-details/pd-details.vue'

export default {
  emits: ['close', 'saved'],

  components: {
    PdDetails,
  },

  props: {
    participant: {
      type: Object,
      default: null,
    },
    raceEvent: {
      type: Object,
      required: true,
    },
  },

  provide() {
    return {
      activeRaceEvent: toRef(this, 'raceEvent'),
      raceEvent: computed(() => this.raceEvent),
      races: computed(() => this.races),
      commitChangedAttributes: this.commitChangedAttributes,
    }
  },

  data() {
    return {
      model: null,
      error: '',
    }
  },

  computed: {
    title() {
      if (!this.model) return 'Participant'
      if (!this.model.id || String(this.model.id).startsWith('NEW-')) return 'New Participant'
      return `${this.model.lastName || ''}, ${this.model.firstName || ''}`
    },

    races() {
      return (this.raceEvent?.races || []).filter(Boolean)
    },
  },

  watch: {
    participant: {
      immediate: true,
      handler() {
        this.model = this.participant ?
          createModelProxy(this.participant, schema.participant, this) :
          null
        this.error = ''
      },
    },
  },

  methods: {
    async commitChangedAttributes() {
      if (!this.model?._changedAttributes?.size) return

      try {
        await this.model._commitValues()
        this.$emit('saved', this.participant)
      }
      catch (err) {
        this.error = err?.response?.data?.error || err?.message || 'Could not save participant.'
        throw err
      }
    },

    async modelDidChange(obj, attr) {
      const oneShotAttrs = ['status', 'raceId', 'waveId']
      if (!oneShotAttrs.includes(attr)) return

      await this.commitChangedAttributes()
    },

    modelSourceDidChange() {
      return true
    },
  },
}
</script>
